import { useCallback, useContext } from "react";

import { Button, Callout, Drawer } from "@blueprintjs/core";

import { useWorkspaceSlug } from "@/components/helpers/custom-hooks/use-workspace-slug";
import { DialogContext } from "@/components/layout/DialogProvider";
import { Flex } from "@/components/layout/flexbox";
import { BasicTab } from "@/components/pages_logged_in/roleplays/details/scenario/edit/BasicTab";
import { CharacterTab } from "@/components/pages_logged_in/roleplays/details/scenario/edit/CharacterTab";
import { ScenarioTab } from "@/components/pages_logged_in/roleplays/details/scenario/edit/ScenarioTab";
import { useEditScenario } from "@/components/pages_logged_in/roleplays/details/scenario/edit/useEditScenario";
import { Tabs } from "@/components/pieces/Tabs";
import { LIGHTEST_BACKGROUND } from "@/css/constants";
import { ListScenario } from "@/types/roleplays";

export function EditScenario({
  initialTab,
  onClose,
  scenario,
}: {
  initialTab: "basic" | "scenario" | "character" | null;
  scenario: ListScenario;
  onClose: (isSuccess?: boolean) => void;
}) {
  const {
    state,
    dispatch,
    handleSave,
    loading,
    disabled,
    isDirty,
    handlePreviewClick,
  } = useEditScenario(scenario, onClose, initialTab);
  const { showConfirmationDialog } = useContext(DialogContext);
  const workspaceSlug = useWorkspaceSlug();

  const handleClose = useCallback(() => {
    if (isDirty) {
      showConfirmationDialog({
        title: "Save Changes?",
        intent: "primary",
        description:
          "Would you like to save the changes you've made to the scenario before leaving this page?",
        onConfirm: handleSave,
        onCancel: onClose,
        confirmLabel: "Save Changes",
        cancelLabel: "Discard Changes",
      });
    } else {
      onClose();
    }
  }, [isDirty, onClose, handleSave, showConfirmationDialog]);

  return (
    <Drawer
      isOpen={!!initialTab}
      usePortal={true}
      lazy={true}
      onClose={handleClose}
      title="Edit"
      style={{
        maxWidth: "600px",
        width: "100%",
        height: "100%",
      }}
    >
      {state.error && <Callout intent="danger">{state.error}</Callout>}
      <Flex flexDirection="column" overflowY="auto" className="flex-auto">
        <div className="border-b border-gray-300 bg-blue-50 px-6">
          <Tabs
            backgroundColor={LIGHTEST_BACKGROUND}
            tabs={[
              { key: "basic", label: "Basic Details" },
              { key: "scenario", label: "Scenario Details" },
              { key: "character", label: "Character Details" },
            ]}
            tab={state.tab}
            onTabChange={(tab) => {
              dispatch({ type: "setTab", tab });
            }}
          />
        </div>
        {state.tab.key === "basic" && (
          <BasicTab state={state} dispatch={dispatch} />
        )}
        {state.tab.key === "scenario" && (
          <ScenarioTab
            state={state}
            dispatch={dispatch}
            onPreviewClick={handlePreviewClick}
          />
        )}
        {state.tab.key === "character" && (
          <CharacterTab
            persona={state.persona}
            dispatch={dispatch}
            photoEndpoint={`${API_HOST}/scenario_character_photos/${scenario?.slug}?workspace_slug=${workspaceSlug}`}
          />
        )}
      </Flex>
      <Flex justifyContent="flex-start" p={24} className="bts">
        <Button
          loading={loading}
          intent="primary"
          large={true}
          onClick={handleSave}
          disabled={disabled}
        >
          Save
        </Button>
      </Flex>
    </Drawer>
  );
}
